import { Field, useFormikContext } from 'formik';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import { FormErrorUI, FormLabelUI, InputUI } from '../../Interface';
import { BpType } from '../../../config/company';
import postalCodeService from '../../../service/postalCode.service';
import useParam from '../../../context/ParamContext';

const CompanyAddDialogFields = ({ expandedAccordionsState }) => {
  const { params } = useParam();
  const { expandedAccordions, setExpandedAccordions } = expandedAccordionsState;
  const { errors, values, setFieldValue } = useFormikContext();

  const getCityByPostalCode = (postalCode) => {
    const city = values.address?.[0]?.city;
    if (!postalCode || city) return;

    postalCodeService.getRow(postalCode).then((data) => {
      if (data?.defaultCity) {
        setFieldValue('address.0.city', data.defaultCity);
      }
    });
  };

  const isCompanyType = values.bpType === BpType.COMPANY;
  const companyRoleFieldShowed = params.SIMPLE_CONTACT_CREATE_SHOW_ROLE_FIELD === 'Y';
  const contactFieldMd = companyRoleFieldShowed ? 3 : 4;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={isCompanyType ? 6 : 12}>
        <Field
          type="text"
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Partner neve" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.name} />}
          InputLabelProps={{ shrink: values.name && values.name !== '' }}
          onBlur={(e) => {
            if (values.bpType === BpType.PRIVATE) {
              setFieldValue('contact.0.name', e.target.value);
            }
          }}
        />
      </Grid>

      {isCompanyType && (
        <Grid item xs={12} md={6}>
          <Field
            type="text"
            name="hunTaxNumber"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Magyar adószám" />}
            variant="outlined"
            size="small"
            helperText={<FormErrorUI message={errors.hunTaxNumber} />}
            InputLabelProps={{
              shrink: values.hunTaxNumber && values.hunTaxNumber !== '',
            }}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Accordion
          expanded={expandedAccordions.contact}
          onChange={(_, expanded) => {
            setExpandedAccordions((prev) => ({
              ...prev,
              contact: expanded,
            }));
          }}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: '12px !important',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <AccordionSummary tabIndex={-1} expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PersonIcon />
              <Typography fontWeight="bold">Kapcsolattartó</Typography>
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6} md={contactFieldMd}>
                <Field
                  type="text"
                  name="contact.0.name"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Név" />}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={6} md={contactFieldMd}>
                <Field
                  type="text"
                  name="contact.0.phoneNumber"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Telefonszám" />}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={contactFieldMd}>
                <Field
                  type="text"
                  name="contact.0.email"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="E-mail cím" />}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              {companyRoleFieldShowed && (
                <Grid item xs={12} md={3}>
                  <Field
                    type="text"
                    name="contact.0.role"
                    as={InputUI}
                    fullWidth
                    label={<FormLabelUI text="Szerep" />}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Accordion
          expanded={expandedAccordions.address}
          onChange={(_, expanded) => {
            setExpandedAccordions((prev) => ({
              ...prev,
              address: expanded,
            }));
          }}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: '12px !important',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <AccordionSummary tabIndex={-1} expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocationOnIcon />
              <Typography fontWeight="bold">Cím adatok</Typography>
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                <Field
                  type="text"
                  name="address.0.postCode"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Irányítószám" />}
                  variant="outlined"
                  size="small"
                  onBlur={(e) => getCityByPostalCode(e.target.value)}
                />
              </Grid>

              <Grid item xs={6} md={4}>
                <Field
                  type="text"
                  name="address.0.city"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Város" />}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Field
                  type="text"
                  name="address.0.streetAddress"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Utca, házszám" />}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};

export default CompanyAddDialogFields;
